'use client';

import { FunctionComponent, ReactNode } from 'react';

import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import { useLocale } from '@/react/hooks/useLocale';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { Accordion } from '../../../core-ui/Accordion';
import { Breakpoint, Breakpoints } from '../../../core-ui/Breakpoints';
import { IconAnimations, IconTypes } from '../../../core-ui/Icon';
import { Link } from '../../../core-ui/Link';

import S from './styles.base.module.scss';

/**
 * List of links pulled from navigation elements.
 */
export const CategoryLinks: FunctionComponent = () => {
  const { language, country } = useLocale();
  const desktop: Array<ReactNode> = [];
  const mobile: Array<ReactNode> = [];

  const config = ConfigurationService.getConfig('footer', language, country);
  const footerMenu = config.getSetting('footerMenu').value;
  // Retrieve ecommerce related values
  const ecommerceConfig = ConfigurationService.getConfig(
    'ecommerce',
    language,
    country
  );
  const ecommerceEnabled = ecommerceConfig.getSetting('ecommerceEnabled').value;
  const keys = ecommerceConfig
    .getSetting('ecommerceKeys')
    .value.map((key) => key as unknown as string);

  // Icon types depend on brand
  const AccordionIconType = useBrandLocaleValue<IconTypes>(
    () => ({
      default: IconTypes.ChevronDown,
      AHNU: IconTypes.ChevronDownLight
    }),
    []
  );

  footerMenu.forEach((header) => {
    // If the header is the key for an ecommerce enabled component, and ecommerce is disabled, skip it.
    const headerId: string = header.id.value;
    if (keys.some((key) => headerId === key) && !ecommerceEnabled) {
      return;
    }
    const list = (
      <ul className={S.links}>
        {header.links.value?.map(
          ({ id, label, href, newTab, needsLogin, prefetch }) =>
            // If the link ID is the key for an ecommerce enabled component, and ecommerce is disabled, skip it.
            keys.some((key) => id.value === key) && !ecommerceEnabled ? null : (
              <li className={S.item} key={id.value}>
                <Link
                  className={S.link}
                  variant="link"
                  href={needsLogin.value ? '/login' : href.value}
                  openInNewTab={newTab.value}
                  prefetch={prefetch.value}
                >
                  {label.value}
                </Link>
              </li>
            )
        )}
      </ul>
    );

    desktop.push(
      <div className={S.footerBlock} key={header.label.value}>
        <div className={S.blockHeader}>{header.label.value}</div>
        {list}
      </div>
    );

    mobile.push(
      <div className={S.accordion} key={header.label.value}>
        <Accordion
          title={header.label.value}
          titleClassName={`${S.blockHeader} ${S.accordionHeader}`}
          iconDown={AccordionIconType}
          iconDownAnimation={IconAnimations.CounterClockwise}
          iconUp={AccordionIconType}
          iconUpAnimation={IconAnimations.Clockwise}
        >
          {list}
        </Accordion>
      </div>
    );
  });

  return (
    <Breakpoints>
      <Breakpoint media="desktop">{desktop}</Breakpoint>
      <Breakpoint default>{mobile}</Breakpoint>
    </Breakpoints>
  );
};
